import React, { useMemo } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { useQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'

import { CenteredIndicatorWidget } from '../components/MetricWidget'
import { HistoryAvailabilityGraphWidget } from '../components/Graphs'
import { SWITCH_REACHABILITY_GRAPH } from '../../../../operations-gql/queries'
import { buildHistorySegments, formatDecimal } from '../utils'

const HistoryBar = ({ history }) => {
  const segments = useMemo(() => buildHistorySegments(history), [history])

  return (
    <Box
      sx={{
        display: 'flex',
        width: 150,
        maxWidth: '100%',
        height: 8,
        borderRadius: '16px',
        overflow: 'hidden',
        backgroundColor: '#F7F7F7'
      }}
    >
      {segments.map(segment => (
        <Box
          key={segment.key}
          sx={{
            width: `${segment.width}%`,
            height: '100%',
            backgroundColor: segment.up === null ? '#DCDCDC' : segment.up ? '#92BC62' : '#DC2626',
            borderRadius: '16px',
            flexShrink: 0
          }}
        />
      ))}
    </Box>
  )
}

const PoeDisplayBar = ({ measured, reserved, max }) => {
  const measuredValue = Number(measured)
  const reservedValue = Number(reserved)
  const maxValue = Number(max)
  const hasValues = Number.isFinite(maxValue) && maxValue > 0
  const measuredPercent = hasValues && Number.isFinite(measuredValue) ? Math.max(0, Math.min((measuredValue / maxValue) * 100, 100)) : 0
  const reservedPercent = hasValues && Number.isFinite(reservedValue) ? Math.max(0, Math.min((reservedValue / maxValue) * 100, 100)) : 0
  const reservedLabelLeft = 30 + (reservedPercent / 100) * 120 - 10

  return (
    <Box sx={{ position: 'relative', width: 180, maxWidth: '100%', height: 42, pt: '9px', pb: '14px' }}>
      <Typography
        sx={{
          position: 'absolute',
          bottom: 0,
          left: reservedLabelLeft,
          color: '#464646',
          fontSize: 10,
          lineHeight: '10px',
          whiteSpace: 'nowrap'
        }}
      >
        {Number.isFinite(reservedValue) ? `${formatDecimal(reservedValue)}W` : '-'}
      </Typography>
      <Stack direction="row" alignItems="center" gap={0.75}>
        <Typography sx={{ color: '#464646', fontSize: 10, lineHeight: '10px', minWidth: 24, textAlign: 'left' }}>
          {Number.isFinite(measuredValue) ? `${formatDecimal(measuredValue)}W` : '-'}
        </Typography>
        <Box sx={{ position: 'relative', flex: 1, height: 8, borderRadius: '16px', backgroundColor: '#F7F7F7' }}>
          <Box sx={{ position: 'absolute', left: `${reservedPercent}%`, top: 0, width: 4, height: 8, borderRadius: '16px', backgroundColor: '#F59E0B' }}>
            <Box sx={{ position: 'absolute', left: 1, bottom: -5, height: 4, borderLeft: '2px solid #F59E0B' }} />
          </Box>
          <Box
            sx={{
              width: `${measuredPercent}%`,
              height: 8,
              borderRadius: '16px',
              backgroundColor: '#92BC62'
            }}
          />
        </Box>
        <Typography sx={{ color: '#464646', fontSize: 10, lineHeight: '10px', minWidth: 24, textAlign: 'right' }}>
          {Number.isFinite(maxValue) ? `${formatDecimal(maxValue)}>` : '-'}
        </Typography>
      </Stack>
    </Box>
  )
}

const SwitchSections = ({ equipment }) => {
  const { t } = useTranslation()
  const { data, loading } = useQuery(SWITCH_REACHABILITY_GRAPH, {
    variables: { mac: equipment?.mac, timeRange: { preset: '-1d' } },
    skip: !equipment?.mac
  })
  const history = data?.switchHistory || equipment?.history_up
  const poeMeasured = equipment.poe?.measured
  const poeMax = equipment.poe?.max
  const summaryHeight = 280

  return (
    <>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '65% minmax(0, 35%)' }, gap: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <HistoryAvailabilityGraphWidget
            history={history}
            title={t('supervision.hardware.switch.availability')}
            loading={loading}
            sx={{ minHeight: summaryHeight, height: '100%' }}
            modalQuery={SWITCH_REACHABILITY_GRAPH}
            modalVariables={{ mac: equipment?.mac }}
            modalDataKey="switchHistory"
          />
        </Box>
        <Box sx={{ minWidth: 0, display: 'grid', gridTemplateColumns: '1fr', gridTemplateRows: 'repeat(2, minmax(0, 1fr))', gap: 2 }}>
          <CenteredIndicatorWidget title={t('supervision.hardware.switch.history_24h')} icon="fa-solid fa-clock-three">
            <HistoryBar history={history} />
          </CenteredIndicatorWidget>
          <CenteredIndicatorWidget title={t('supervision.hardware.switch.poe_state')} icon="fa-solid fa-bolt">
            {poeMax ? (
              <PoeDisplayBar measured={poeMeasured} reserved={equipment.poe?.reserved} max={poeMax} />
            ) : (
              <Typography sx={{ fontSize: 11, color: '#989898' }}>-</Typography>
            )}
          </CenteredIndicatorWidget>
        </Box>
      </Box>
    </>
  )
}

export default SwitchSections
